import { Alert, Button, Drawer, Spinner } from "@station/ui";
import { createFileRoute, Link, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppCommandPalette } from "../components/shell/app-command-palette.js";
import { Sidebar } from "../components/shell/sidebar.js";
import { Topbar } from "../components/shell/topbar.js";
import { AccountBanners } from "../features/platform/account.js";
import { portalAccess, staffRouteRedirect } from "../features/portal/portal-values.js";
import { useGlobalShortcut } from "../lib/hooks.js";
import { RealtimeProvider } from "../lib/realtime-context.js";
import { loadScope } from "../lib/scope.js";
import { bootstrapSession, ensureMe, useMe } from "../lib/session.js";
import { StepUpProvider } from "../lib/step-up.js";
import { useMenuContext, useWorkspace } from "../lib/use-workspace.js";

export const Route = createFileRoute("/_app")({
  // Oturum yoksa girişe yönlendir; girişten sonra kaldığı sayfaya dönsün.
  beforeLoad: async ({ location }) => {
    if (!(await bootstrapSession())) throw redirect({ to: "/giris", search: { redirect: location.href } });
    // Filo portalı kullanıcısı (yalnızca cari kapsamlı atamalar — ADR 0017 §17) personel kabuğunu görmez: kendi hesap
    // güvenliği / profil sayfası dışındaki her adresten, kabuk çizilmeden portala gönderilir.
    const me = await ensureMe().catch(() => null);
    // Kiracı üyeliği olmayan platform personeli (destek oturumu dışında) doğrudan platform paneline gider.
    if (me && me.user.platformRole && !me.supportSession && me.memberships.length === 0)
      throw redirect({ to: "/platform", replace: true });
    if (me && staffRouteRedirect(location.pathname, portalAccess(me.activeTenant)) !== null)
      throw redirect({ to: "/portal", replace: true });
  },
  component: AppShell,
});

function AppShell() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useGlobalShortcut(
    "mod+k",
    useCallback(() => setPaletteOpen((open) => !open), []),
  );

  const tenant = me.data?.activeTenant ?? null;
  useEffect(() => loadScope(tenant?.id ?? null), [tenant?.id]);
  const menuContext = useMenuContext();
  const workspace = useWorkspace(menuContext);

  if (!me.data) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }

  // Portal kullanıcısı buraya yalnızca kendi hesap sayfaları için gelir (yönlendirme `beforeLoad`'dadır).
  const portal = portalAccess(tenant);

  const onWizard = location.pathname === "/kurulum";
  const needsOnboarding =
    tenant !== null && !tenant.onboardingCompleted && menuContext.permissions.has("org.company.manage");
  const needsMfa = me.data.mfaSetupRequired && location.pathname !== "/ayarlar/guvenlik";

  return (
    // Kiracı değişince alt ağaç yeniden kurulur: WebSocket yeni token'la bağlanır, yerel durumlar sıfırlanır.
    <RealtimeProvider key={tenant?.id ?? "no-tenant"}>
      <StepUpProvider>
        <a href="#main-content" className="skip-link">
          {t("shell.skipToContent")}
        </a>
        <div className="flex min-h-dvh">
          <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 border-r lg:block print:!hidden">
            <Sidebar context={menuContext} workspace={workspace} />
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">
            <Topbar
              me={me.data}
              onOpenMenu={() => setMobileMenuOpen(true)}
              onOpenPalette={() => setPaletteOpen(true)}
              createActions={workspace.createActions}
            />
            <main
              id="main-content"
              tabIndex={-1}
              className="mx-auto w-full max-w-7xl flex-1 p-4 outline-none sm:p-6"
            >
              {tenant === null ? (
                <Alert tone="warning" title={t("shell.noTenant.title")}>
                  {t("shell.noTenant.description")}
                </Alert>
              ) : null}
              {/* Destek oturumu / etkin destek erişimi / salt okunur (ödeme nedeniyle) / önemli duyuru (Prompt 24). */}
              <AccountBanners me={me.data} />
              {needsMfa ? (
                <Alert tone="warning" title={t("shell.mfaRequired.title")} className="mb-4">
                  <p>{t("shell.mfaRequired.description")}</p>
                  <Button asChild size="sm" className="mt-2">
                    <Link to="/ayarlar/guvenlik">{t("shell.mfaRequired.action")}</Link>
                  </Button>
                </Alert>
              ) : null}
              {portal.portalOnly ? (
                <Alert tone="info" title={t("portal.backToPortalTitle")} className="mb-4">
                  <Button asChild size="sm" className="mt-2">
                    <Link to="/portal">{t("portal.backToPortal")}</Link>
                  </Button>
                </Alert>
              ) : null}
              {needsOnboarding && !onWizard && !needsMfa && !portal.portalOnly ? (
                <Alert tone="info" title={t("shell.onboarding.title")} className="mb-4">
                  <p>{t("shell.onboarding.description")}</p>
                  <Button asChild size="sm" className="mt-2">
                    <Link to="/kurulum">{t("shell.onboarding.action")}</Link>
                  </Button>
                </Alert>
              ) : null}
              <Outlet />
            </main>
          </div>
        </div>
        <Drawer
          open={mobileMenuOpen}
          onOpenChange={setMobileMenuOpen}
          title={t("shell.mainNavigation")}
          className="left-0 right-auto max-w-72 border-l-0 border-r"
        >
          <div className="-mx-6 -my-4">
            <Sidebar
              context={menuContext}
              workspace={workspace}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>
        </Drawer>
        <AppCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} context={menuContext} />
      </StepUpProvider>
    </RealtimeProvider>
  );
}
